import React, { useState, useContext, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { AuthContext } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Toast from './Toast';
import AuthModal from './AuthModal';
import { addCustomActivity, bookTour } from '../services/api';
import { FormControl, InputLabel, Select, MenuItem, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Info as InfoIcon } from '@mui/icons-material';
import useTranslation from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import PayPalTourProvider from './PayPalTourProvider';
import { PayPalButtons } from "@paypal/react-paypal-js";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow: hidden;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 12px;
  width: 70%;
  height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  overflow: hidden;
  position: relative;
  max-width: 1400px;

  @media (max-width: 768px) {
    width: 95%;
    height: 90vh;
    margin: 10px;
  }
`;

const Title = styled.h2`
  color: #0A2429;
  text-align: left;
  margin: 0;
  font: normal normal bold 30px/40px Poppins;
  letter-spacing: 0px;
  font-style: normal;

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const ImageSection = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  min-height: 300px;

  @media (max-width: 768px) {
    flex: none;
    height: 30vh;
    min-height: 200px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  transition: transform 0.5s ease-in-out;
  transform: translateX(-${props => props.currentIndex * 100}%);
`;

const Image = styled.div`
  flex: 0 0 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const ImageNavigationButtons = styled.div`
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
  z-index: 2;
`;

const ImageNavigationButton = styled.button`
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoSection = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  position: relative;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 15px;
  }
`;

const Description = styled.p`
  color: #0A2429;
  text-align: left;
  font: normal normal medium 16px/25px Poppins;
  letter-spacing: 0px;
  margin: 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: ${props => props.expanded ? 'unset' : '3'};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  color: #316C6E;
  font-size: 14px;
  padding: 5px 0;
  cursor: pointer;
  margin-top: 5px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 24px 0;
  padding: 0 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const DateSelect = styled(FormControl)`
  flex: 2;
  min-width: 200px;
  
  .MuiOutlinedInput-root {
    border-radius: 25px;
    background-color: #f8f9fa;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: #f0f1f2;
    }
    
    &.Mui-focused {
      background-color: white;
    }
  }

  .MuiSelect-select {
    padding: 12px 20px;
  }
`;

const BookTourButton = styled.button`
  flex: 1;
  min-width: 150px;
  padding: 12px 20px;
  background-color: #F38530;
  color: #FFFFFF;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  text-transform: uppercase;
  font: normal normal 600 16px/25px Poppins;
  letter-spacing: 0.5px;
  
  &:hover {
    background-color: #d67428;
  }

  &:disabled {
    background-color: #F38530;
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SectionCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin: 15px 0;
  padding: 15px;
`;

const DetailSection = styled.div`
  padding: 24px 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }

  h3 {
    color: #316C6E;
    margin: 0 0 16px 0;
    font-size: 1.2rem;
  }
`;

const StyledList = styled(List)`
  padding: 0;
`;

const StyledListItem = styled(ListItem)`
  padding: 4px 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1002;
  font-size: 20px;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  @media (max-width: 768px) {
    top: 15px;
    right: 15px;
  }
`;

const PriceTag = styled.div`
  background-color: #316C6E;
  color: white;
  padding: 6px 12px;
  border-radius: 15px;
  display: inline-flex;
  align-items: center;
  margin: 8px 0;
  font-size: 0.95rem;
  box-shadow: 0 2px 4px rgba(49, 108, 110, 0.2);
  letter-spacing: 0.2px;
  
  span {
    margin-left: 4px;
    font-size: 0.75rem;
    opacity: 0.85;
  }
`;

const TourModal = ({ tour, onClose, open }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [autoMove, setAutoMove] = useState(true);
  const [showMore, setShowMore] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const { isAuthenticated } = useContext(AuthContext);
  const [showPayment, setShowPayment] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(null);

  const images = tour?.image_url ? (
    typeof tour.image_url === 'string' ? 
      JSON.parse(tour.image_url) : 
      tour.image_url
  ) : [];

  useEffect(() => {
    let timer;
    if (open && autoMove && images.length > 1) {
      timer = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 5000);
    }
    return () => clearInterval(timer);
  }, [autoMove, images, open]);

  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('/api/exchange-rates/latest');
        const data = await response.json();
        setExchangeRate(parseFloat(data.usd_rate));
      } catch (error) {
        console.error('Error fetching exchange rate:', error);
        // Fallback to a default rate if API fails
        setExchangeRate(60.17);
      }
    };
    fetchExchangeRate();
  }, []);

  if (!open || !tour) {
    return null;
  }

  const handlePrevImage = () => {
    setAutoMove(false);
    setCurrentImageIndex((prevIndex) => 
      (prevIndex - 1 + images.length) % images.length
    );
  };

  const handleNextImage = () => {
    setAutoMove(false);
    setCurrentImageIndex((prevIndex) => 
      (prevIndex + 1) % images.length
    );
  };

  const handleBookTour = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (!selectedDate) {
      setToastMessage('Please select a date for the tour');
      setToastType('error');
      setShowToast(true);
      return;
    }

    setShowPayment(true);
  };

  const handlePaymentSuccess = async (paypalOrderId) => {
    try {
      // Book the tour first
      const selectedTourDate = tour.dates.find(date => date.date === selectedDate);
      if (!selectedTourDate) {
        throw new Error('Selected date not found');
      }

      const bookingResponse = await bookTour({
        tourId: tour.id,
        tourDateId: selectedTourDate.id,
        paypalOrderId: paypalOrderId
      });

      setToastMessage('Tour booked successfully! Redirecting to your trip...');
      setToastType('success');
      setShowToast(true);
      
      setTimeout(() => {
        window.location.href = `/trip/${bookingResponse.itineraryId}`;
      }, 2000);
    } catch (error) {
      console.error('Error booking tour:', error);
      setToastMessage('Failed to book tour. Please try again.');
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    handleBookTour();
  };

  const parseJsonArray = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return jsonString ? [jsonString] : [];
    }
  };

  const included = parseJsonArray(tour.included);
  const notIncluded = parseJsonArray(tour.notIncluded);
  const tips = parseJsonArray(tour.tips);

  const renderPayPalButtons = () => {
    if (!showPayment || !exchangeRate) return null;

    const usdAmount = (tour.price / exchangeRate).toFixed(2);

    return (
      <PayPalTourProvider>
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>
              {t('components.tourModal.completePayment')}
            </h3>
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>Total: RD$ {new Intl.NumberFormat('en-US', {
                  style: 'decimal',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(tour.price)}</strong>
              </div>
              <div style={{ fontSize: '0.9em', color: '#666' }}>
                (USD ${usdAmount} at rate: RD$ {exchangeRate})
              </div>
            </div>
            <PayPalButtons
              forceReRender={[tour.price, exchangeRate]}
              createOrder={(data, actions) => {
                return actions.order.create({
                  purchase_units: [{
                    amount: {
                      value: usdAmount,
                      currency_code: 'USD'
                    },
                    description: `Tour: ${tour.name}`
                  }],
                  application_context: {
                    shipping_preference: 'NO_SHIPPING'
                  }
                });
              }}
              onApprove={async (data, actions) => {
                setIsProcessingPayment(true);
                try {
                  console.log('PayPal order approved:', data);
                  await handlePaymentSuccess(data.orderID);
                  setShowPayment(false);
                } catch (error) {
                  console.error('Payment error:', error);
                  setToastMessage('Payment failed. Please try again.');
                  setToastType('error');
                  setShowToast(true);
                } finally {
                  setIsProcessingPayment(false);
                }
              }}
              onError={(err) => {
                console.error('PayPal error:', err);
                setToastMessage('Payment failed. Please try again.');
                setToastType('error');
                setShowToast(true);
                setShowPayment(false);
              }}
              onCancel={() => {
                setToastMessage('Payment cancelled.');
                setToastType('info');
                setShowToast(true);
                setShowPayment(false);
              }}
              style={{
                layout: 'vertical',
                color: 'blue',
                shape: 'rect',
                label: 'pay'
              }}
            />
            <button 
              onClick={() => setShowPayment(false)}
              style={{
                width: '100%',
                padding: '10px',
                marginTop: '10px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#f0f0f0',
                cursor: 'pointer'
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </PayPalTourProvider>
    );
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        <Content>
          <ImageSection>
            <ImageWrapper currentIndex={currentImageIndex}>
              {images.map((image, index) => (
                <Image key={index} image={image} />
              ))}
            </ImageWrapper>
            {images.length > 1 && (
              <ImageNavigationButtons>
                <ImageNavigationButton onClick={handlePrevImage}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </ImageNavigationButton>
                <ImageNavigationButton onClick={handleNextImage}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </ImageNavigationButton>
              </ImageNavigationButtons>
            )}
          </ImageSection>

          <InfoSection>
            <Title>{tour.name}</Title>
            <PriceTag>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(tour.price)}
              <span>DOP</span>
            </PriceTag>
            <Description expanded={showMore}>
              {tour.description}
            </Description>
            {tour.description?.length > 100 && (
              <ShowMoreButton onClick={() => setShowMore(!showMore)}>
                {showMore ? t('components.tourModal.showLess') : t('components.tourModal.showMore')}
              </ShowMoreButton>
            )}

            <ButtonsContainer>
              {tour.dates && tour.dates.length > 0 ? (
                <>
                  <DateSelect>
                    <InputLabel>{t('components.tourModal.selectDate')}</InputLabel>
                    <Select
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      label={t('components.tourModal.selectDate')}
                    >
                      <MenuItem value="">
                        <em>{t('components.tourModal.chooseDatePlaceholder')}</em>
                      </MenuItem>
                      {tour.dates.map((date) => (
                        <MenuItem key={date.id} value={date.date}>
                          {format(new Date(date.date), 'MMMM d, yyyy', { 
                            locale: language === 'es' ? es : undefined 
                          })} ({date.current_stock} {date.current_stock === 1 
                            ? t('components.tourModal.spotLeft')
                            : t('components.tourModal.spotsLeft')
                          })
                        </MenuItem>
                      ))}
                    </Select>
                  </DateSelect>
                  <BookTourButton 
                    onClick={handleBookTour}
                    disabled={!selectedDate}
                    style={{ opacity: selectedDate ? 1 : 0.6 }}
                  >
                    {t('components.tourModal.bookTour')}
                  </BookTourButton>
                </>
              ) : (
                <div style={{ textAlign: 'center', color: '#666' }}>
                  {t('components.tourModal.noAvailableDates')}
                </div>
              )}
            </ButtonsContainer>

            {included.length > 0 && (
              <DetailSection>
                <h3>{t('components.tourModal.included')}</h3>
                <StyledList dense>
                  {included.map((item, index) => (
                    <StyledListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={item} sx={{ m: 0 }} />
                    </StyledListItem>
                  ))}
                </StyledList>
              </DetailSection>
            )}

            {notIncluded.length > 0 && (
              <DetailSection>
                <h3>{t('components.tourModal.notIncluded')}</h3>
                <StyledList dense>
                  {notIncluded.map((item, index) => (
                    <StyledListItem key={index}>
                      <ListItemIcon>
                        <CancelIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={item} sx={{ m: 0 }} />
                    </StyledListItem>
                  ))}
                </StyledList>
              </DetailSection>
            )}

            {tour.meetingPoint && (
              <DetailSection>
                <h3>{t('components.tourModal.meetingPoint')}</h3>
                <p>{tour.meetingPoint}</p>
              </DetailSection>
            )}

            {tips.length > 0 && (
              <DetailSection>
                <h3>{t('components.tourModal.tips')}</h3>
                <StyledList dense>
                  {tips.map((tip, index) => (
                    <StyledListItem key={index}>
                      <ListItemIcon>
                        <InfoIcon color="info" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={tip} sx={{ m: 0 }} />
                    </StyledListItem>
                  ))}
                </StyledList>
              </DetailSection>
            )}
          </InfoSection>
        </Content>
        {renderPayPalButtons()}
      </ModalContent>

      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={handleAuthSuccess}
        />
      )}

      {showToast && (
        <Toast 
          message={toastMessage}
          onClose={() => setShowToast(false)}
          type={toastType}
        />
      )}
    </ModalOverlay>
  );
};

export default TourModal; 